import { CLASSES } from "../content/classes";
import { abilityById } from "../content/abilities";
import { nextId } from "./combat";
import type { ClassId, TeamId, Unit, World } from "./types";
import { UNIT_RADIUS } from "./types";

export function makeUnit(
  world: World,
  opts: {
    team: TeamId;
    name: string;
    classId: ClassId;
    pos: { x: number; y: number };
    isPlayer?: boolean;
    facing?: number;
    ownerId?: number;
    kind?: Unit["kind"];
    hp?: number;
  },
): Unit {
  const def = CLASSES[opts.classId];
  const skill = def.skill ? abilityById(def.skill) : null;
  const ult = def.ult ? abilityById(def.ult) : null;
  const kind = opts.kind ?? (opts.classId === "spirit" ? "spirit" : opts.classId === "skeleton" || opts.classId === "golem" ? "minion" : "hero");
  const unit: Unit = {
    id: nextId(world),
    team: opts.team,
    name: opts.name,
    classId: opts.classId,
    kind,
    pos: { x: opts.pos.x, y: opts.pos.y },
    prevPos: { x: opts.pos.x, y: opts.pos.y },
    radius: kind === "spirit" ? 0.32 : kind === "minion" ? 0.36 : UNIT_RADIUS,
    hp: opts.hp ?? def.maxHp,
    maxHp: opts.hp ?? def.maxHp,
    facing: opts.facing ?? (opts.team === 0 ? 0 : Math.PI),
    baseSpeed: def.speed,
    isPlayer: !!opts.isPlayer,
    dead: false,
    respawnIn: 0,
    respawnSecs: 7,
    spawn: { x: opts.pos.x, y: opts.pos.y },
    ownerId: opts.ownerId ?? null,
    basicCd: 0,
    basicCdMax: def.basicCd,
    skillCd: 0,
    skillCdMax: skill?.cd ?? 0,
    ultCd: 0,
    ultCdMax: ult?.cd ?? 0,
    casting: null,
    charging: null,
    lockout: 0,
    statuses: [],
    shields: [],
    lastAttackAt: -99,
    lastMoveAt: -99,
    stillFor: 0,
    movedThisTick: false,
    seenAt: {},
    ambushGrace: {},
    state: {
      weapon: 0,
      charge: 0,
      poisonCharges: 0,
      blockReady: 1,
      blockCd: 0,
      selfCorpse: 0,
      spiritTimer: 4,
      spawnSpirit: 0,
    },
    damageDealt: 0,
    damageTaken: 0,
    healsDone: 0,
    autoTargetId: null,
  };
  world.units.push(unit);
  if (opts.isPlayer) world.playerId = unit.id;
  return unit;
}

export function spawnMinion(world: World, owner: Unit, classId: "skeleton" | "golem", x: number, y: number): Unit {
  return makeUnit(world, {
    team: owner.team,
    name: classId === "golem" ? "Golem" : "Skeleton",
    classId,
    pos: { x, y },
    ownerId: owner.id,
    kind: "minion",
    facing: owner.facing,
  });
}

export function spawnSpirit(world: World, owner: Unit, x: number, y: number, hp?: number): Unit {
  const s = makeUnit(world, {
    team: owner.team,
    name: "Spirit",
    classId: "spirit",
    pos: { x, y },
    ownerId: owner.id,
    kind: "spirit",
    hp: hp ?? 4,
  });
  s.maxHp = Math.max(s.hp, 6);
  return s;
}

export function pruneDeadSummons(world: World): void {
  world.units = world.units.filter((u) => {
    if ((u.kind === "minion" || u.kind === "spirit") && u.dead) return false;
    return true;
  });
}
